import { inject, Injectable } from '@angular/core';

import { CanvasElementType } from '@blaze-runtime/engine-domain';
import { CanvasElementDataProperties, FieldSortConfig } from '@blaze-shared/elements-domain';
import { compare, compareAscBy, compareDescBy } from '@blaze-shared/platform-util';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { DatasetRow } from '@blaze-shared/schema-domain';
import { hasNodeWithSorting, Workflow } from '@blaze-shared/workflow-domain';

import { ElementDefinitionsService } from '../../../../../services/element-definitions.service';

/** For element types with `optionsSortSupport`, sorts default data source binding results by the options label field */
@Injectable({ providedIn: 'root' })
export class SortResultsService {
  private readonly elementDefinitionsService = inject(ElementDefinitionsService);
  private readonly schemaService = inject(SchemaService);

  sortIfApplicable(
    elementType: CanvasElementType | undefined,
    binding: CanvasElementDataProperties | undefined,
    data: DatasetRow[] | null,
    fieldSortConfig?: FieldSortConfig | FieldSortConfig[],
    workflow?: Workflow,
  ): DatasetRow[] {
    let sortField = '';

    if (elementType) {
      const hasOptionsSortSupport =
        this.elementDefinitionsService.getFeature(elementType, 'optionsSortSupport') === true;
      const defaultDataSourceInputName =
        this.elementDefinitionsService.getDefaultDataSourceInput(elementType)?.name;

      if (hasOptionsSortSupport && binding?.key === defaultDataSourceInputName) {
        sortField = this.schemaService.getColumnName(binding?.bindings?.label) ?? '';
      }
    }

    if (!data) {
      return [];
    }

    if (!sortField || (workflow && hasNodeWithSorting(workflow)) || data.length <= 1) {
      return data;
    }

    const sortConfig = (
      Array.isArray(fieldSortConfig) ? fieldSortConfig : [fieldSortConfig]
    ).filter((sc): sc is FieldSortConfig => !!sc?.field);

    if (sortConfig.length <= 1) {
      const sc = sortConfig[0];
      const sortDirection = sc?.direction ?? 'asc';
      if (sc) {
        sortField = this.schemaService.getColumnName(sc.field) ?? '';
      }
      return data.toSorted((x, y) => compare(x[sortField], y[sortField], sortDirection === 'asc'));
    }

    // multi-field sorting
    const sorters = sortConfig.map((sc) => {
      const fieldName = this.schemaService.getColumnName(sc.field);
      if (!fieldName) {
        throw Error(`Sorting error: Column ${sc.field} not found`);
      }
      return sc.direction === 'asc' ? compareAscBy(fieldName) : compareDescBy(fieldName);
    });
    const sorted = [...data].toSorted((x, y) => {
      let sortResult = 0;
      for (const sorter of sorters) {
        sortResult = sorter(x, y);
        if (sortResult !== 0) {
          return sortResult;
        }
      }
      return sortResult;
    });
    return sorted;
  }
}
